import React, { useState, useEffect } from 'react';
import {
	Modal,
	Box,
	Typography,
	TextField,
	Button,
	CircularProgress,
} from '@mui/material';
import { toast } from 'react-toastify';
import AgentsServices from 'services/AgentsServices';

const AgentDetailApprovalStatus = ({
	isOpen,
	onClose,
	title,
	message = '',
	historyData,
	fetchAgents, // ← add this
}) => {
	const [remark, setRemark] = useState('');
	const [error, setError] = useState('');
	const [loading, setLoading] = useState(false);
	const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);

	useEffect(() => {
		if (!isOpen) {
			setRemark('');
			setError('');
			setIsRejectModalOpen(false);
		}
	}, [isOpen]);
	const handleResponse = (res, status, isError = false) => {
		const mainMsg =
			res?.message ||
			(status === 1
				? 'Agent approved successfully'
				: 'Agent rejected successfully');
		const optionalMsg = res?.data?.message || res?.error?.message || '';
		const msg = optionalMsg ? `${mainMsg}\n\nDetails: ${optionalMsg}` : mainMsg;

		if (isError) {
			toast.error(msg);
		} else {
			toast.success(msg);

			//   Refresh agents list if available
			if (typeof fetchAgents === 'function') {
				fetchAgents();
			}

			setIsRejectModalOpen(false);
			onClose();
		}
	};

	const handleSubmit = async (status) => {
		if (loading) return;

		if (status === 2 && !remark.trim()) {
			setError('Remark is required!');
			return;
		}

		setLoading(true);

		try {
			const payload = {
				history_id: historyData?.[0]?.id,
				is_approved: status,
				...(status === 2 && { reject_remark: remark.trim() }),
			};

			const response = await AgentsServices.AgentProfileApprovalUpdate(payload);
			handleResponse(response, status);
		} catch (err) {
			handleResponse(err, status, true);
		} finally {
			setLoading(false);
		}
	};

	return (
		<>
			{/* Main Modal */}
			<Modal open={isOpen} onClose={onClose}>
				<Box
					sx={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						bgcolor: 'background.paper',
						boxShadow: 24,
						p: 4,
						width: 400,
						borderRadius: 2,
					}}
				>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
						<Typography variant="h6">{title}</Typography>
						<button
							onClick={onClose}
							style={{
								background: 'transparent',
								border: 'none',
								cursor: 'pointer',
								fontSize: 20,
							}}
						>
							✕
						</button>
					</Box>

					{message && (
						<Typography
							sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}
						>
							{message}
						</Typography>
					)}

					<Box sx={{ display: 'flex', justifyContent: 'center' }}>
						<Button
							variant="text"
							onClick={() => setIsRejectModalOpen(true)}
							sx={{ color: 'red', mr: 2 }}
							disabled={loading}
						>
							Reject
						</Button>
						<Button
							variant="text"
							onClick={() => handleSubmit(1)}
							sx={{ color: 'green' }}
							disabled={loading}
						>
							{loading ? <CircularProgress size={20} /> : 'Approve'}
						</Button>
					</Box>
				</Box>
			</Modal>

			{/* Reject Modal */}
			<Modal
				open={isRejectModalOpen}
				onClose={() => setIsRejectModalOpen(false)}
			>
				<Box
					sx={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						bgcolor: 'background.paper',
						boxShadow: 24,
						p: 4,
						width: 400,
						borderRadius: 2,
					}}
				>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
						<Typography variant="h6">Reason for Rejection</Typography>
						<button
							onClick={() => setIsRejectModalOpen(false)}
							style={{
								background: 'transparent',
								border: 'none',
								cursor: 'pointer',
								fontSize: 20,
							}}
						>
							✕
						</button>
					</Box>

					<TextField
						value={remark}
						onChange={(e) => {
							setRemark(e.target.value);
							setError('');
						}}
						label="Enter rejection reason"
						fullWidth
						variant="outlined"
						error={!!error}
						helperText={error}
						inputProps={{ maxLength: 200 }}
						sx={{ mb: 2 }}
					/>

					<Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
						<Button
							variant="text"
							onClick={() => setIsRejectModalOpen(false)}
							sx={{ color: 'red', mr: 2 }}
							disabled={loading}
						>
							Cancel
						</Button>
						<Button
							variant="text"
							onClick={() => handleSubmit(2)}
							sx={{ color: 'green' }}
							disabled={loading}
						>
							{loading ? <CircularProgress size={20} /> : 'Submit'}
						</Button>
					</Box>
				</Box>
			</Modal>
		</>
	);
};

export default React.memo(AgentDetailApprovalStatus);
